/**
 * Defensive guard for AGENT message content.
 *
 * The thread agent is supposed to store only its `message` field as the entry
 * content. A malformed or legacy agent response can instead leak the entire
 * `AgentOutput` JSON envelope — e.g.
 * `{ "actions": [], "message": "…", "message_reason": "clarification", "references": [] }`
 * — into the stored content, which then renders as raw JSON in the chat bubble.
 *
 * `stripAgentEnvelope` scans for the first balanced `{…}` object that has the
 * shape of an envelope (a string `message` plus an envelope-distinctive key)
 * and returns its inner `message`. It mirrors the backend parser, so prose with
 * surrounding/trailing braces — or a stray brace before the real JSON — is
 * handled the same way. For normal markdown/text it returns the content
 * unchanged, and it never throws.
 *
 * Apply this only to AGENT_RESPONSE content — user/external contributions are
 * raw and must never be rewritten.
 */

/** Yield each balanced `{…}` object in `text`, ignoring braces inside strings. */
function* balancedObjects(text: string): Generator<string> {
    let i = 0;
    const n = text.length;
    while (i < n) {
        if (text[i] !== "{") {
            i++;
            continue;
        }
        let depth = 0;
        let inString = false;
        let escaped = false;
        let end = -1;
        for (let j = i; j < n; j++) {
            const ch = text[j];
            if (inString) {
                if (escaped) escaped = false;
                else if (ch === "\\") escaped = true;
                else if (ch === '"') inString = false;
                continue;
            }
            if (ch === '"') inString = true;
            else if (ch === "{") depth++;
            else if (ch === "}") {
                depth--;
                if (depth === 0) {
                    end = j;
                    break;
                }
            }
        }
        if (end !== -1) {
            yield text.slice(i, end + 1);
            i = end + 1;
        } else {
            // Unbalanced from here — advance one char and keep scanning.
            i++;
        }
    }
}

/** True if `data` has the distinctive shape of an AgentOutput envelope. */
const isAgentEnvelope = (data: unknown): data is { message: string } =>
    data !== null &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    typeof (data as Record<string, unknown>).message === "string" &&
    ("actions" in data || "message_reason" in data || "references" in data);

export const stripAgentEnvelope = (content: string): string => {
    if (!content) return content;
    for (const candidate of balancedObjects(content)) {
        let parsed: unknown;
        try {
            parsed = JSON.parse(candidate);
        } catch {
            continue;
        }
        if (isAgentEnvelope(parsed)) return parsed.message;
    }
    return content;
};

/** Posted by the Confirm/Cancel buttons; shared with the renderer so they can't drift. */
export const PROPOSAL_CONFIRM_MESSAGE = "Confirmed — go ahead.";
export const PROPOSAL_CANCEL_MESSAGE = "No — cancel that.";
